import { isCssVarColor } from './isCssColor.ts';
import type { Color, IntColorRGBA } from '../../types/Color.d.ts';
import { clamp } from '../math/clamp.ts';
import { percentageToValue } from '../math/percentageToValue.ts';

/**
 * Converts 0-1 float to 0-255 integer.
 */
function to255Integer(value: float): int {
  return clamp(percentageToValue(value, 0, 255), 0, 255);
}

/**
 * Converts color to an array with 4 numbers, from 0 to 255, for RGBA. E.g., [255, 0, 0, 255]
 *
 * If the input is missing alpha information, it is assumed to be 255.
 * @function
 * @param {Color} color
 * @return {Number[]}
 */
export function toComponentArray(color: Color): IntColorRGBA | undefined {
  if (!color || isCssVarColor(color)) return undefined;

  if (typeof color === 'string') return parseString(color);

  if (!Array.isArray(color)) throw new Error(`Cannot parse color: ${color}`);

  // Integer array
  if (Number.isSafeInteger(color[0])) {
    return [
      color[0],
      color[1],
      color[2],
      color[3] ?? 255
    ];
  }

  // Decimal array
  return [
    to255Integer(color[0]),
    to255Integer(color[1]),
    to255Integer(color[2]),
    to255Integer(color[3] ?? 1)
  ];
}

function parseString(color: string): IntColorRGBA {
  const match = color.match(/^#?([\dA-F]{2})([\dA-F]{2})([\dA-F]{2})([\dA-F]{2})?$/i);

  if (!match) {
    console.warn(color, ' is not a valid color string. Returning [0, 0, 0, 0].');

    return [0, 0, 0, 0];
  }

  const [, r, g, b, a = 'FF'] = match;
  const values = [r, g, b, a].map(v => Number.parseInt(v, 16));

  return [values[0], values[1], values[2], values[3]];
}